import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

const MAX_CHARS = 500;
const CREDITS_PER_SMS = 5;

export default function SendMessage() {
  const [vehicles, setVehicles] = useState([]);
  const [credits, setCredits] = useState(0);
  const [form, setForm] = useState({ senderVehicleId: '', recipientRegistration: '', message: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.vehicles.list(), api.payments.credits()])
      .then(([v, c]) => {
        setVehicles(v);
        setCredits(c.credits);
        if (v.length > 0) setForm(f => ({ ...f, senderVehicleId: v[0].id }));
      })
      .catch(console.error);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({
      ...f,
      [name]: name === 'recipientRegistration' ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.senderVehicleId) return setError('Please register a vehicle first');
    setLoading(true);
    try {
      const data = await api.messages.send(form);
      setSuccess(data.message);
      setCredits(data.credits);
      setForm(f => ({ ...f, recipientRegistration: '', message: '' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const charsLeft = MAX_CHARS - form.message.length;
  const canSend = credits >= CREDITS_PER_SMS;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Send a Message</h1>
        <p className="page-subtitle">Reach the owner of any registered vehicle via SMS</p>
      </div>

      {/* ── DO NOT TEXT WHILE DRIVING warning ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'rgba(255,82,82,0.08)',
        border: '1px solid rgba(255,82,82,0.35)',
        borderRadius: 12,
        padding: '14px 18px',
        marginBottom: 20,
      }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>⊘</span>
        <p style={{
          fontSize: 14,
          fontWeight: 700,
          color: '#ff5252',
          lineHeight: 1.5,
          margin: 0,
          letterSpacing: 0.2,
        }}>
          Do not use TxtMyCar while driving.
          <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6 }}>
            Always pull over and stop safely before sending a message.
          </span>
        </p>
      </div>

      {/* ── Credits indicator ── */}
      <div style={{
        background: canSend ? 'var(--accent-bg)' : 'rgba(255,82,82,0.05)',
        border: `1px solid ${canSend ? 'var(--accent-border)' : 'rgba(255,82,82,0.3)'}`,
        borderRadius: 12,
        padding: '12px 16px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 10,
      }}>
        <span style={{ fontSize: 14, color: 'var(--muted)' }}>
          Credit balance:{' '}
          <strong style={{ color: canSend ? 'var(--accent)' : 'var(--danger)' }}>
            {credits} credits
          </strong>
          <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
            ({CREDITS_PER_SMS} per message)
          </span>
        </span>
        {!canSend && (
          <Link to="/topup" className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }}>
            Top up now
          </Link>
        )}
      </div>

      {/* ── Form ── */}
      <div className="card" style={{ maxWidth: 560 }}>
        {vehicles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#128664;</div>
            <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
              You need to register a vehicle before sending messages.
            </p>
            <Link to="/vehicles" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              Register a vehicle
            </Link>
          </div>
        ) : (
          <>
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">&#10003; {success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Send from (your vehicle)</label>
                <select name="senderVehicleId" value={form.senderVehicleId} onChange={handleChange} required>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.registration}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Send to (their registration)</label>
                <input
                  type="text"
                  name="recipientRegistration"
                  value={form.recipientRegistration}
                  onChange={handleChange}
                  placeholder="AB12 CDE"
                  maxLength={8}
                  required
                  style={{ textTransform: 'uppercase', letterSpacing: 2, fontFamily: 'monospace', fontSize: 16 }}
                />
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="e.g. Your vehicle is blocking my driveway, could you please move it?"
                  maxLength={MAX_CHARS}
                  required
                  rows={5}
                />
                <div style={{
                  fontSize: 12, textAlign: 'right', marginTop: 4,
                  color: charsLeft < 50 ? 'var(--danger)' : 'var(--muted)',
                }}>
                  {charsLeft} characters remaining
                </div>
              </div>

              {/* Sender reg reminder */}
              <div style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontSize: 13,
                color: 'var(--muted)',
                padding: '12px 16px',
                marginBottom: 20,
                lineHeight: 1.6,
              }}>
                The recipient will receive your message along with your registration number{' '}
                <strong style={{ color: 'var(--accent)' }}>
                  {vehicles.find(v => v.id === form.senderVehicleId)?.registration}
                </strong>{' '}
                so they know who it's from.
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading || !canSend}>
                {loading
                  ? <><span className="spinner"></span> Sending...</>
                  : `Send SMS (${CREDITS_PER_SMS} credits)`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
